import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signUpWithEmail, signInWithGoogle } from '../../lib/supabase';
import { useToast } from '../../components/Toast';

const normalizeWebsitePrefill = (value: string | null): string => {
  if (!value) return '';
  return value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
};

export function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const marketingWebsite = useMemo(
    () => normalizeWebsitePrefill(new URLSearchParams(location.search).get('website')),
    [location.search],
  );

  // Going back from Google restores this page from the back/forward cache, "Creating account..." included.
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => { if (e.persisted) setLoading(false); };
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await signUpWithEmail(email, password, firstName, lastName, marketingWebsite || undefined);

    if (error) {
      toast.error(error.message);
      setLoading(false);
    } else {
      // Redirect to verification page
      navigate('/verification', { state: { email } });
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);

    if (marketingWebsite) {
      sessionStorage.setItem('lima_marketing_prefill_website', marketingWebsite);
    }

    const { error } = await signInWithGoogle();

    if (error) {
      toast.error(error.message);
      setLoading(false);
    }
    // Google OAuth will redirect
  };

  return (
    <>
      <div className="text-align-center">
        <h1 className="text-size-huge">Join Lima</h1>
        <div className="spacer-tiny"></div>
        <div className="text-size-regular text-colour-alt-grey">
          Already have an account?{' '}
          <Link to="/login" className="dark-link">
            Login
          </Link>
        </div>
      </div>

      <div className="spacer-large"></div>

      <div className="auth-block">
        <div className="account-block w-form">
          <form onSubmit={handleEmailSignup}>
            {/* Google Sign Up */}
            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading}
              className="button flex-btn is-white is-large has-inner-shadow"
              style={{ width: '100%' }}
            >
              <img
                src="/images/Google__G__logo.svg"
                loading="lazy"
                width={16}
                alt="Google logomark"
              />
              <div>Sign up with Google</div>
            </button>

            <div className="or-divider">
              <div className="line-split"></div>
              <div className="text-size-tiny text-colour-light-grey">or</div>
              <div className="line-split"></div>
            </div>

            {/* Name Fields */}
            <div className="form-col-wrapper">
              <div className="col-half">
                <label htmlFor="firstName" className="form_label">
                  First Name
                </label>
                <input
                  className="form_input w-input"
                  maxLength={256}
                  name="firstName"
                  placeholder="Joe"
                  type="text"
                  id="firstName"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="col-half">
                <label htmlFor="lastName" className="form_label">
                  Last Name
                </label>
                <input
                  className="form_input w-input"
                  maxLength={256}
                  name="lastName"
                  placeholder="Smith"
                  type="text"
                  id="lastName"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="spacer-small"></div>

            {/* Email Field */}
            <label htmlFor="email" className="form_label">
              Email
            </label>
            <input
              className="form_input is-light w-input"
              maxLength={256}
              name="email"
              placeholder="example@email.com"
              type="email"
              id="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <div className="spacer-small"></div>

            {/* Password Field */}
            <label htmlFor="password" className="form_label">
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="form_input is-light w-input"
                maxLength={256}
                name="password"
                placeholder=""
                type={showPassword ? 'text' : 'password'}
                id="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--slate-brown)',
                  opacity: showPassword ? 1 : 0.5,
                }}
                tabIndex={-1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>

            <div className="spacer-small"></div>

            {/* Submit Button */}
            <input
              type="submit"
              data-wait="Please wait..."
              className="button is-wide is-large w-button"
              value={loading ? 'Creating account...' : 'Sign up'}
              disabled={loading}
            />
          </form>
        </div>
      </div>

      <div className="text-align-center">
        <div className="spacer-xsmall"></div>
        <div className="text-size-small text-colour-alt-grey">
          By signing up to uselima.com, you agree to our{' '}
          <a
            href="https://www.uselima.com/terms"
            target="_blank"
            rel="noreferrer"
            className="link-underline"
          >
            Terms
          </a>{' '}
          &{' '}
          <a
            href="https://www.uselima.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="link-underline"
          >
            Privacy
          </a>{' '}
          policies
        </div>
      </div>
    </>
  );
}
